import type { ReactNode } from 'react';
import { GATEWAY_BASE_URL } from '../config/endpoints.ts';
import { useT } from '../i18n/index.ts';

/**
 * How-it-works — DESIGN.md §6.8.
 *
 * Three equal cards, each with a large `cream`-tinted Geist numeral
 * bleeding off the bottom-right corner as decoration. Per-step CTA is
 * intentionally absent — the page's CTA is the Closing section.
 *
 * Step 3 interpolates the live gateway base URL so the on-page text
 * stays consistent with whatever Vite env this build was compiled
 * against — no risk of the endpoint drifting away from what tenants
 * will actually see in their dashboards.
 */
export function HowItWorks(): JSX.Element {
  const t = useT();
  const baseUrl = GATEWAY_BASE_URL;
  return (
    <section id="how" className="py-28" data-testid="how-it-works">
      <div className="mx-auto max-w-7xl px-6">
        <div className="mx-auto mb-16 max-w-2xl text-center">
          <p className="mb-4 font-mono text-[11px] font-medium uppercase tracking-[0.22em] text-claude">
            {t('how.eyebrow')}
          </p>
          <h2 className="text-[40px] font-semibold leading-[1.05] text-ink md:text-[52px]">
            {t('how.title.lead')}
            <span className="serif text-claude">{t('how.title.accent')}</span>
          </h2>
        </div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          <Step
            numeral="1"
            titleKey="how.step1.title"
            body={t('how.step1.body')}
            testId="step-1"
          />
          <Step
            numeral="2"
            titleKey="how.step2.title"
            body={t('how.step2.body')}
            testId="step-2"
          />
          <Step
            numeral="3"
            titleKey="how.step3.title"
            testId="step-3"
            body={
              <>
                {t('how.step3.body.before')}
                <code className="ghost mx-0.5 rounded bg-butter px-1.5 py-0.5 font-mono text-[13px] text-ink">
                  {baseUrl}
                </code>
                {t('how.step3.body.after')}
              </>
            }
          />
        </div>
      </div>
    </section>
  );
}

interface StepProps {
  readonly numeral: string;
  readonly titleKey: 'how.step1.title' | 'how.step2.title' | 'how.step3.title';
  readonly body: ReactNode;
  readonly testId: string;
}

function Step({ numeral, titleKey, body, testId }: StepProps): JSX.Element {
  const t = useT();
  return (
    <div
      className="ghost lift relative overflow-hidden rounded-3xl bg-linen p-8"
      data-testid={testId}
    >
      <span
        className="serif pointer-events-none absolute -bottom-6 -right-2 select-none text-[140px] font-bold leading-none text-cream"
        aria-hidden="true"
      >
        {numeral}
      </span>
      <h3 className="relative mb-3 text-[22px] font-semibold tracking-tight-ink text-ink">
        {t(titleKey)}
      </h3>
      <p className="relative text-[15px] leading-[1.7] text-ink-muted">{body}</p>
    </div>
  );
}
